'use client'
import React, { useState } from 'react'
import { AiFillCloseSquare } from 'react-icons/ai'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import tailwindConfig from '@tailwind.config'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import { Dialog } from '@headlessui/react'
import 'swiper/css'
import 'swiper/css/navigation'

const BLUR = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAAAyCAYAAACqNX6+AAAAXUlEQVR42u3RMQEAAAgDoJnc6FrB0wMqUMl0eKOECEGIEIQIQYgQhAgRIgQhQhAiBCFCECIEIQgRghAhCBGCECEIQYgQhAhBiBCECEEIQoQgRAhChCBECEIQIoSrBfgKSs//8LAxAAAAAElFTkSuQmCC"

// ─── Close button ──────────────────────────────────────────────────────────────
function CloseBtn({ onClose }) {
  return (
    <button
      onClick={onClose}
      className="absolute top-2 right-2 z-50 bg-maincolor rounded-md overflow-hidden hover:opacity-80 transition-opacity"
    >
      <AiFillCloseSquare color={tailwindConfig.theme.extend.colors.light} size={40} />
    </button>
  )
}

// ─── Style: simple carousel ────────────────────────────────────────────────────
function PopupCarousel({ gallery, initialSlide, onClose, imageRadius = '0.5rem' }) {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1000
  return (
    <div className="fixed left-1/2 -translate-x-1/2 sm:translate-y-[60px] translate-y-[40px] z-[1000] inset-0 flex sm:w-[1000px] w-full aspect-[4/3] items-center justify-center overflow-hidden">
      <Dialog.Panel className="relative w-full h-full group">
        <CloseBtn onClose={onClose} />
        <Swiper
          className="w-full h-full overflow-hidden"
          navigation={width > 450}
          modules={[Navigation]}
          initialSlide={initialSlide || 0}
        >
          {gallery.map((image, i) => (
            <SwiperSlide key={i} className="flex items-center justify-center">
              <div
                className="relative w-full aspect-[4/3] bg-black"
                style={{ borderRadius: imageRadius, overflow: 'hidden' }}
              >
                <Image
                  src={image}
                  alt={`Image ${i + 1}`}
                  fill
                  className="object-contain"
                  placeholder="blur"
                  blurDataURL={BLUR}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </Dialog.Panel>
    </div>
  )
}

// ─── Style: multi-image cols (large left + 3 right col) ───────────────────────
function PopupCols({ gallery, initialSlide, onClose, imageRadius = '0.5rem' }) {
  const [active, setActive] = useState(initialSlide || 0)
  const prev = () => setActive(a => Math.max(0, a - 1))
  const next = () => setActive(a => Math.min(gallery.length - 1, a + 1))

  // show 3 thumbnails centered around active
  const thumbStart = Math.max(0, Math.min(active - 1, gallery.length - 3))
  const thumbs = gallery.slice(thumbStart, thumbStart + 3)

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-10">
      <Dialog.Panel className="relative w-full max-w-[1100px] bg-black overflow-hidden" style={{ borderRadius: imageRadius }}>
        <CloseBtn onClose={onClose} />
        <div className="flex flex-col sm:flex-row w-full">
          {/* Main image */}
          <div
            className="relative w-full sm:w-3/4 aspect-[4/3] bg-black"
            style={{ borderRadius: imageRadius, overflow: 'hidden' }}
          >
            <Image
              src={gallery[active]}
              alt={`Image ${active + 1}`}
              fill
              className="object-contain"
              placeholder="blur"
              blurDataURL={BLUR}
              priority
            />
            {/* Prev/Next */}
            <button onClick={prev} disabled={active === 0} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-1 disabled:opacity-20 transition-all">
              <MdChevronLeft size={28} />
            </button>
            <button onClick={next} disabled={active === gallery.length - 1} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-1 disabled:opacity-20 transition-all">
              <MdChevronRight size={28} />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
              {active + 1} / {gallery.length}
            </div>
          </div>
          {/* Right col: 3 thumbs */}
          <div className="w-full sm:w-1/4 flex flex-row sm:flex-col gap-1 p-1">
            {thumbs.map((img, i) => {
              const idx = thumbStart + i
              return (
                <div
                  key={idx}
                  onClick={() => setActive(idx)}
                  className={`relative flex-1 aspect-[4/3] cursor-pointer overflow-hidden transition-all ${idx === active ? 'ring-2 ring-white' : 'opacity-60 hover:opacity-90'}`}
                  style={{ borderRadius: imageRadius }}
                >
                  <Image
                    src={img}
                    alt={`Thumb ${idx + 1}`}
                    fill
                    className="object-cover"
                    placeholder="blur"
                    blurDataURL={BLUR}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </Dialog.Panel>
    </div>
  )
}

// ─── Style: multi-image rows (large top + row of thumbs below) ────────────────
function PopupRows({ gallery, initialSlide, onClose, imageRadius = '0.5rem' }) {
  const [active, setActive] = useState(initialSlide || 0)
  const prev = () => setActive(a => Math.max(0, a - 1))
  const next = () => setActive(a => Math.min(gallery.length - 1, a + 1))

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-10">
      <Dialog.Panel className="relative w-full max-w-[1100px] bg-black overflow-hidden" style={{ borderRadius: imageRadius }}>
        <CloseBtn onClose={onClose} />
        {/* Main image */}
        <div
          className="relative w-full aspect-[16/9] bg-black"
          style={{ borderRadius: imageRadius, overflow: 'hidden' }}
        >
          <Image
            src={gallery[active]}
            alt={`Image ${active + 1}`}
            fill
            className="object-contain"
            placeholder="blur"
            blurDataURL={BLUR}
            priority
          />
          <button onClick={prev} disabled={active === 0} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-1 disabled:opacity-20 transition-all">
            <MdChevronLeft size={28} />
          </button>
          <button onClick={next} disabled={active === gallery.length - 1} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-1 disabled:opacity-20 transition-all">
            <MdChevronRight size={28} />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
            {active + 1} / {gallery.length}
          </div>
        </div>
        {/* Thumb row */}
        <div className="flex flex-row gap-1 p-1 overflow-x-auto">
          {gallery.map((img, i) => (
            <div
              key={i}
              onClick={() => setActive(i)}
              className={`relative flex-shrink-0 w-20 aspect-[4/3] cursor-pointer overflow-hidden transition-all ${i === active ? 'ring-2 ring-white' : 'opacity-50 hover:opacity-80'}`}
              style={{ borderRadius: imageRadius }}
            >
              <Image
                src={img}
                alt={`Thumb ${i + 1}`}
                fill
                className="object-cover"
                placeholder="blur"
                blurDataURL={BLUR}
              />
            </div>
          ))}
        </div>
      </Dialog.Panel>
    </div>
  )
}

// ─── Main Gallery component ────────────────────────────────────────────────────
const Gallery = ({ isOpen, onClose, gallery, videoUrl, initialSlide, altName, popupStyle = 'carousel', imageRadius = '0.5rem' }) => {
  if (!gallery?.length) return null

  return (
    <Dialog className="w-full h-full z-[1000] inset-0" open={isOpen} onClose={onClose}>
      <div className="fixed inset-0 bg-black/80 z-[1000]" aria-hidden="true" onClick={onClose} />

      {popupStyle === 'carousel' && (
        <PopupCarousel gallery={gallery} initialSlide={initialSlide} onClose={onClose} imageRadius={imageRadius} />
      )}
      {popupStyle === 'cols' && (
        <PopupCols gallery={gallery} initialSlide={initialSlide} onClose={onClose} imageRadius={imageRadius} />
      )}
      {popupStyle === 'rows' && (
        <PopupRows gallery={gallery} initialSlide={initialSlide} onClose={onClose} imageRadius={imageRadius} />
      )}
    </Dialog>
  )
}

export default Gallery